import { useEffect, useMemo, useState } from "react";
import * as THREE from "three";
import { listZones, type ZoneRow } from "../api/rest";
import { useUIStore } from "../stores/uiStore";
import { tokens } from "../styles/tokens";

interface Props {
  siteId: string;
  warehouseId: string;
}

/**
 * Floor markings for the warehouse's traffic/safety `zone` rows (ADR 143) —
 * fetched from the zones API instead of the static demo seed Warehouse.tsx
 * used to bundle at build time, so /live finally shows the same zones the
 * structure page lists (and whatever an external editor writes via the API).
 *
 * Same container shape as OccupancyLayerContainer: plain effect + fetch (the
 * TanStack provider doesn't cross the R3F renderer boundary), mounted from
 * Scene.tsx behind `layers.zones` with a `key={siteId:warehouseId}` remount,
 * errors render nothing rather than crashing the canvas.
 *
 * Sits at the 0.02 m overlay height (shared with HeatmapLayer's plane);
 * OccupancyLayer stacks above at 0.03 m.
 */
const FLOOR_Y = 0.02;

/**
 * kind → token color, dimmed like the original ZoneFloor markings. Every
 * `zone.kind` CHECK value gets a color so externally-authored zones always
 * render; unknown kinds fall back to neutral ink.
 */
export const ZONE_KIND_COLOR: Record<string, string> = {
  restricted: tokens.semantic.danger,
  pedestrian: tokens.semantic.warn,
  dock:       tokens.semantic.info,
  staging:    tokens.blue[300],
  aisle:      tokens.ink[500],
  one_way:    tokens.blue[500],
};

export function ZonesLayer({ siteId, warehouseId }: Props) {
  const [zones, setZones] = useState<ZoneRow[] | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const selectedZone = useUIStore((s) => s.selectedZone);
  const simDestinationId = useUIStore((s) =>
    s.simulation && s.simulation.warehouseId === warehouseId ? s.simulation.destinationZoneId : null);
  const setSelectedZone = useUIStore((s) => s.setSelectedZone);

  useEffect(() => {
    if (!siteId || !warehouseId) return;
    let cancelled = false;
    listZones(siteId, warehouseId)
      .then((rows) => { if (!cancelled) setZones(rows); })
      .catch(() => { if (!cancelled) setZones(null); });
    return () => { cancelled = true; };
  }, [siteId, warehouseId]);

  // ADR 278: the zone selection lives only while its layer does — toggling
  // zones off or switching warehouse (key remount) must not leave the dock
  // inspecting a zone the scene no longer draws (same rule as selectedBin on
  // OccupancyLayerContainer teardown).
  useEffect(() => () => { useUIStore.getState().setSelectedZone(null); }, []);

  const items = useMemo(
    () => (zones ?? [])
      .filter((z) => Array.isArray(z.polygon) && z.polygon.length >= 3)
      .map((z) => ({ zone: z, shape: polygonToShape(z.polygon as number[][]) })),
    [zones],
  );

  if (items.length === 0) return null;
  return (
    <group rotation={[-Math.PI / 2, 0, 0]} position={[0, FLOOR_Y, 0]}>
      {items.map(({ zone, shape }) => {
        const base = ZONE_KIND_COLOR[zone.kind] ?? tokens.ink[500];
        // ADR 290: the simulation's destination zone reads as picked — the
        // what-if's target must be findable at a glance while the panel is up.
        const picked = zone.id === selectedZone?.id || zone.id === simDestinationId;
        // ADR 279: hover is a half-step toward the picked look — the zone
        // under the cursor brightens a little, which is what says "this is
        // clickable" without shouting. Picked stays the loudest.
        const hovered = !picked && zone.id === hoveredId;
        // The picked zone renders at full token color and brighter emissive —
        // the dock below names it, but the floor must show WHICH one it names.
        const color = new THREE.Color(base).multiplyScalar(picked ? 0.7 : hovered ? 0.55 : 0.4).getStyle();
        return (
          <mesh
            key={zone.id}
            receiveShadow
            onClick={(e) => {
              e.stopPropagation();
              // ADR 280: capture the zone's WORLD box at click time — the mesh
              // rides the warehouse layout transform, so the polygon's raw
              // coordinates would frame the wrong spot on a transformed map.
              // A few meters of headroom keep the racks in frame; jsdom has no
              // real Object3D behind the event, hence the guard.
              let box: number[] | null = null;
              if (e.object && typeof (e.object as THREE.Object3D).updateWorldMatrix === "function") {
                const b = new THREE.Box3().setFromObject(e.object);
                box = [b.min.x, 0, b.min.z, b.max.x, Math.max(b.max.y, 5), b.max.z];
              }
              setSelectedZone(zone, box);
            }}
            onPointerOver={() => { setHoveredId(zone.id); document.body.style.cursor = "pointer"; }}
            onPointerOut={() => {
              setHoveredId((id) => (id === zone.id ? null : id));
              document.body.style.cursor = "auto";
            }}
          >
            <shapeGeometry args={[shape]} />
            <meshStandardMaterial
              color={color}
              emissive={color}
              emissiveIntensity={picked ? 0.35 : hovered ? 0.2 : 0.1}
              transparent
              opacity={picked ? 0.8 : hovered ? 0.68 : 0.55}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>
        );
      })}
    </group>
  );
}

/**
 * `[[x,y]]` meters → floor-plane shape. Same y-negation as OccupancyLayer's
 * polygonToShape (see its doc comment): the shared −π/2 ground rotation maps
 * local (x, y) to world (x, −y), so negating y here lands the polygon at
 * world (x, *, y) like every other floor consumer. DoubleSide on the material
 * keeps either polygon winding visible.
 */
function polygonToShape(polygon: number[][]): THREE.Shape {
  const shape = new THREE.Shape();
  polygon.forEach(([x, y], i) => {
    if (i === 0) shape.moveTo(x, -y);
    else shape.lineTo(x, -y);
  });
  return shape;
}
